import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, RefreshCw, AlertCircle, CheckCircle, AlertTriangle,
  ChevronDown, ChevronRight, ExternalLink, Clock, TrendingUp,
  Shield, DollarSign, BarChart3, Search, Loader, Users, SlidersHorizontal,
  Wrench, Lock
} from 'lucide-react';
import './DiagnosticView.css';
import DiagnosticChecksManager from './DiagnosticChecksManager';
import ModaleCorrection from './ModaleCorrection';
import TexteAccentue from './TexteAccentue';
import { colonnesDesElements } from './colonnesElements';
import googleAdsLogo from '../logos/google-ads.png';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';

// Icônes par famille
const FAMILY_ICONS = {
  hygiene: Shield,
  budget: DollarSign,
  performance: BarChart3,
  tracking: Search
};

// Labels des familles
const FAMILY_LABELS = {
  hygiene: 'Hygiène du compte',
  budget: 'Budget',
  performance: 'Performances',
  tracking: 'Tracking'
};

// Fonction de formatage des valeurs de diagnostic selon le type de check
const formatDiagnosticValue = (value, threshold, checkKey) => {
  if (value === null || value === undefined) return { value: '-', threshold: '-' };

  // S'assurer que les valeurs sont des nombres
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  const numThreshold = typeof threshold === 'number' ? threshold : parseFloat(threshold);

  // Checks avec ratio (Xx)
  const ratioChecks = ['DEVICE_CPA_IMBALANCE', 'BUDGET_VS_CPA_RATIO'];
  if (ratioChecks.includes(checkKey)) {
    return {
      value: isNaN(numValue) ? value : `${numValue.toFixed(1)}x`,
      threshold: isNaN(numThreshold) ? '-' : `${numThreshold}x`
    };
  }

  // Checks avec pourcentage (valeur décimale 0-1 → %)
  const percentChecks = [
    'LOW_OPTIMIZATION_SCORE', 'IMPRESSION_SHARE_DROP', 'IMPRESSION_SHARE_LOST_BUDGET',
    'CPA_SPIKE', 'ROAS_DROP', 'CONVERSION_VOLUME_DROP', 'CTR_DROP', 'CPC_SPIKE',
    'BUDGET_UNDERSPEND', 'BUDGET_CONCENTRATION'
  ];
  if (percentChecks.includes(checkKey)) {
    return {
      value: isNaN(numValue) ? value : `${(numValue * 100).toFixed(1)}%`,
      threshold: isNaN(numThreshold) ? '-' : `${(numThreshold * 100).toFixed(1)}%`
    };
  }

  // Quality Score (sur 10)
  if (checkKey === 'QS_DROP') {
    return {
      value: isNaN(numValue) ? value : `${numValue.toFixed(1)}/10`,
      threshold: isNaN(numThreshold) ? '-' : `${numThreshold}/10`
    };
  }

  // Montants en euros
  const euroChecks = ['DISPLAY_PLACEMENT_NO_EXCLUSIONS', 'PMAX_NEGATIVE_KEYWORD_MISSING'];
  if (euroChecks.includes(checkKey)) {
    return {
      value: isNaN(numValue) ? value : `${numValue.toFixed(1)} €`,
      threshold: isNaN(numThreshold) ? '-' : `${numThreshold.toFixed(1)} €`
    };
  }

  // Par défaut : 1 décimale
  return {
    value: isNaN(numValue) ? value : numValue.toFixed(1),
    threshold: isNaN(numThreshold) ? (threshold || '-') : numThreshold.toFixed(1)
  };
};

// Couleurs des statuts
const STATUS_COLORS = {
  RED: '#ef4444',
  ORANGE: '#f59e0b',
  GREEN: '#22c55e',
  SKIPPED: '#94a3b8'
};

// Mini Score circulaire pour les cartes
const MiniScoreWidget = ({ score }) => {
  const safeScore = score ?? 0;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (safeScore / 100) * circumference;

  let color = '#22c55e';
  let bgColor = '#dcfce7';
  if (safeScore < 50) {
    color = '#ef4444';
    bgColor = '#fee2e2';
  } else if (safeScore < 80) {
    color = '#f59e0b';
    bgColor = '#fef3c7';
  }

  return (
    <div className="mini-score-widget">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle
          cx="45"
          cy="45"
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth="8"
        />
        <circle
          cx="45"
          cy="45"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 45 45)"
          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
        />
      </svg>
      <div className="mini-score-value">
        <span className="score-number" style={{ color }}>{safeScore}</span>
      </div>
    </div>
  );
};

// Carte de compte pour la vue "Tous les comptes"
const AccountCard = ({ accountId, accountName, scan, onClick }) => {
  return (
    <div className="diagnostic-account-card" onClick={onClick}>
      <div className="account-card-score">
        <MiniScoreWidget score={scan?.scoreGlobal} />
      </div>
      <div className="account-card-info">
        <h3 className="account-card-name">{accountName}</h3>
        {scan ? (
          <div className="account-card-counts">
            {scan.statusCounts?.RED > 0 && (
              <span className="count red">
                <AlertCircle size={14} />
                {scan.statusCounts.RED}
              </span>
            )}
            {scan.statusCounts?.ORANGE > 0 && (
              <span className="count orange">
                <AlertTriangle size={14} />
                {scan.statusCounts.ORANGE}
              </span>
            )}
            {scan.statusCounts?.GREEN > 0 && (
              <span className="count green">
                <CheckCircle size={14} />
                {scan.statusCounts.GREEN}
              </span>
            )}
          </div>
        ) : (
          <p className="account-card-empty">Aucun scan</p>
        )}
      </div>
      <div className="account-card-logo">
        <img src={googleAdsLogo} alt="Google Ads" />
      </div>
      <ChevronRight size={20} className="account-card-arrow" />
    </div>
  );
};

// Composant Score circulaire
const ScoreWidget = ({ score }) => {
  const safeScore = score ?? 0;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (safeScore / 100) * circumference;

  let color = '#22c55e'; // Vert
  let bgColor = '#dcfce7'; // Fond vert clair
  if (safeScore < 50) {
    color = '#ef4444'; // Rouge
    bgColor = '#fee2e2'; // Fond rouge clair
  } else if (safeScore < 80) {
    color = '#f59e0b'; // Orange
    bgColor = '#fef3c7'; // Fond orange clair
  }

  return (
    <div className="diagnostic-score-widget">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth="12"
        />
        <circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 70 70)"
          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
        />
      </svg>
      <div className="diagnostic-score-value">
        <span className="score-number" style={{ color }}>{safeScore}</span>
      </div>
    </div>
  );
};

// Composant ligne de check
const DiagnosticCheckRow = ({ result, isExpanded, onToggle, onCorriger }) => {
  const StatusIcon = result.status === 'GREEN' ? CheckCircle :
                    result.status === 'RED' ? AlertCircle :
                    result.status === 'ORANGE' ? AlertTriangle : Clock;

  /*
   * Les colonnes des éléments concernés — déduites de la liste, pas déclarées par contrôle :
   * voir `colonnesElements.js`. Radar lit les mêmes `affectedItems` et le même module, pour que
   * les deux écrans nomment pareil ce qu'ils montrent.
   */
  const elements = result.detail?.affectedItems || [];
  const colonnes = colonnesDesElements(elements);
  // Le lien est un geste, pas une donnée : sa colonne n'existe que si une ligne l'occupe.
  const colonneLien = elements.some(e => e.url);

  return (
    <div className={`diagnostic-check-row status-${result.status.toLowerCase()}`}>
      <div className="check-row-main" onClick={onToggle}>
        <div className="check-status">
          <StatusIcon
            size={20}
            color={STATUS_COLORS[result.status]}
            fill={result.status !== 'SKIPPED' ? STATUS_COLORS[result.status] : 'none'}
            fillOpacity={0.2}
          />
        </div>
        <div className="check-content">
          <div className="check-header">
            <span className="check-label">{result.label}</span>
            {result.periodLabel && (
              <span className="check-period">
                <Clock size={12} />
                {result.periodLabel}
              </span>
            )}
            {result.severity === 'CRITICAL' && result.status === 'RED' && (
              <span className="check-badge critical">CRITIQUE</span>
            )}
          </div>
          {result.value !== null && result.threshold !== null && result.threshold > 0 && (() => {
            const formatted = formatDiagnosticValue(result.value, result.threshold, result.checkKey);
            return (
              <div className="check-value">
                Valeur : {formatted.value} (seuil : {formatted.threshold})
              </div>
            );
          })()}
          {result.status !== 'GREEN' && result.status !== 'SKIPPED' && (
            <div className="check-recommendation">{result.recommendation}</div>
          )}
          {result.detail?.summary && (
            <div className="check-summary">{result.detail.summary}</div>
          )}
        </div>
        {result.detail?.affectedItems?.length > 0 && (
          <div className="check-expand">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        )}
      </div>

      {/*
        Corriger depuis Kokpit, quand le contrôle ouvre un geste. Posé hors de la zone
        dépliable et hors du `onClick` de la ligne : un bouton d'écriture ne doit pas se
        confondre avec le fait de déplier un détail.

        Un contrôle sans action déclarée n'affiche rien — une dérive de CPA ouvre une enquête,
        pas une mutation.
      */}
      {(result.actions || []).filter(a => a.elementsActionnables > 0).length > 0
        && result.status !== 'GREEN' && result.status !== 'SKIPPED' && (
        <div className="check-corrections">
          {result.actions.filter(a => a.elementsActionnables > 0).map(action => (
            result.ecritureAutorisee ? (
              <button
                key={action.cle}
                type="button"
                className="check-corriger"
                onClick={(e) => { e.stopPropagation(); onCorriger(result, action); }}
                title={action.description}
              >
                <Wrench size={14} />
                {action.libelle}
                {/* Les objets modifiés, pas les lignes du constat — voir RadarView. */}
                {action.ciblesDistinctes > 1 && ` (${action.ciblesDistinctes})`}
              </button>
            ) : (
              /* Compte non autorisé : le bouton se montre et explique, plutôt que de
                 disparaître — masquer laisserait croire que Kokpit ne sait pas corriger. */
              <button
                key={action.cle}
                type="button"
                className="check-corriger"
                disabled
                title={`${action.libelle} : l'écriture n'est pas activée sur ce compte. Un administrateur peut l'autoriser dans Admin › MCP.`}
              >
                <Lock size={14} />
                {action.libelle}
              </button>
            )
          ))}
        </div>
      )}

      {isExpanded && elements.length > 0 && (
        <div className="check-details">
          <div className="affected-items">
            <table className="affected-table">
              <thead>
                <tr>
                  {colonnes.map(colonne => (
                    <th
                      key={colonne.entete}
                      scope="col"
                      className={colonne.principale ? 'affected-colonne-principale' : undefined}
                    >
                      {colonne.entete}
                    </th>
                  ))}
                  {colonneLien && <th scope="col" className="affected-colonne-lien" aria-label="Ouvrir dans la régie" />}
                </tr>
              </thead>
              <tbody>
                {elements.map((item, index) => (
                  <tr key={index}>
                    {colonnes.map(colonne => {
                      const texte = colonne.lire(item);
                      return (
                        <td
                          key={colonne.entete}
                          className={[
                            colonne.principale ? 'item-name' : 'item-value',
                            colonne.multiligne ? 'item-multiligne' : null
                          ].filter(Boolean).join(' ')}
                        >
                          {texte === null || texte === undefined || texte === '' ? null : (
                            colonne.lien
                              ? (
                                <a href={texte} target="_blank" rel="noopener noreferrer" title="Ouvrir cette adresse">
                                  {texte}
                                </a>
                              )
                              : <TexteAccentue texte={texte} />
                          )}
                        </td>
                      );
                    })}
                    {colonneLien && (
                      <td className="affected-colonne-lien">
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="item-link">
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Un aperçu plafonné le dit : sans cela, une liste volontairement courte passe pour
              la liste entière, et l'on croit avoir tout vu. */}
          {result.detail?.affectedComplet === false
            && elements.length < (result.detail.affectedCount || 0) && (
            <p className="affected-echantillon">
              {elements.length} des {result.detail.affectedCount} éléments
              concernés sont listés ici.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Composant principal
const DiagnosticView = ({ token, user, selectedClientId, userClients = [], initialAccountId, onInitialAccountUsed, selectedScanId, onScanIdChange, subView, onSubViewChange, hideBreadcrumb = false, isToolsView = false, onFilChange }) => {
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [allScansData, setAllScansData] = useState({});
  const [expandedChecks, setExpandedChecks] = useState({});
  const [collapsedFamilies, setCollapsedFamilies] = useState({});
  const [expandedSections, setExpandedSections] = useState({}); // Pour les sections skipped/passed
  const [scanProgress, setScanProgress] = useState(null); // Progression du scan en cours
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId || null);
  const [allAccounts, setAllAccounts] = useState([]); // Tous les comptes non filtrés
  const [accounts, setAccounts] = useState([]); // Comptes filtrés par client
  /** La correction ouverte, s'il y en a une : `{ result, action }`. */
  const [correction, setCorrection] = useState(null);

  // Filtrer les comptes par client
  const filterAccountsByClient = async (accountsList, clientId) => {
    if (!clientId) return accountsList;

    try {
      const response = await fetch('/api/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        return accountsList.filter(account => {
          return authorizedAccountIds.includes(account.id) ||
                 authorizedAccountIds.includes(account.sourceId);
        });
      }
    } catch (err) {
      console.error('Erreur filtrage comptes:', err);
    }
    return accountsList;
  };

  // Charger les comptes disponibles
  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const response = await fetch('/api/library?usage=outils', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.library) {
        // Les comptes Google Ads sont dans data.library.GOOGLE_ADS
        const googleAdsAccounts = data.library.GOOGLE_ADS || [];
        setAllAccounts(googleAdsAccounts);

        // Filtrer selon le client sélectionné
        const filtered = await filterAccountsByClient(googleAdsAccounts, selectedClientId);
        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filtered].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);
        // "Tous les comptes" est sélectionné par défaut (selectedAccountId reste null)
      }
    } catch (err) {
      console.error('Erreur chargement comptes:', err);
      setError('Erreur lors du chargement des comptes');
    } finally {
      setLoadingAccounts(false);
    }
  }, [token, selectedClientId]);

  // Charger le dernier scan d'un compte
  const fetchLatestScan = useCallback(async (accountId) => {
    if (!accountId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/diagnostic/${accountId}/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        console.log('[DIAGNOSTIC] fetchLatestScan - données reçues:', data.data?.id, 'score:', data.data?.scoreGlobal);
        console.log('[DIAGNOSTIC] resultsByFamily présent:', !!data.data?.resultsByFamily);
        console.log('[DIAGNOSTIC] Familles:', Object.keys(data.data?.resultsByFamily || {}));
        const newScanData = { ...data.data };
        setScanData(newScanData);
        console.log('[DIAGNOSTIC] State setScanData appelé avec:', newScanData.id);
      } else {
        console.log('[DIAGNOSTIC] fetchLatestScan - erreur:', data.error);
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('[DIAGNOSTIC] fetchLatestScan erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger un scan spécifique par son ID
  const fetchScanById = useCallback(async (scanId) => {
    if (!scanId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/diagnostic/scan/${scanId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        console.log('[DIAGNOSTIC] fetchScanById - données reçues:', data.data?.id, 'score:', data.data?.scoreGlobal);
        setScanData({ ...data.data });
      } else {
        console.log('[DIAGNOSTIC] fetchScanById - erreur:', data.error);
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('[DIAGNOSTIC] fetchScanById erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger les scans de tous les comptes
  const fetchAllScans = useCallback(async (accountsList) => {
    if (!accountsList || accountsList.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const scansPromises = accountsList.map(async (account) => {
        try {
          const response = await fetch(`/api/diagnostic/${account.sourceId}/latest`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          return {
            accountId: account.sourceId,
            accountName: account.sourceName || account.sourceId,
            scan: data.success ? data.data : null
          };
        } catch {
          return {
            accountId: account.sourceId,
            accountName: account.sourceName || account.sourceId,
            scan: null
          };
        }
      });

      const results = await Promise.all(scansPromises);
      const scansMap = {};
      results.forEach(r => {
        scansMap[r.accountId] = r;
      });
      setAllScansData(scansMap);
    } catch (err) {
      console.error('[DIAGNOSTIC] fetchAllScans erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Déclencher un scan manuel avec streaming SSE
  const triggerScan = () => {
    if (!selectedAccountId || scanning) return;

    setScanning(true);
    setError(null);
    setScanProgress({ checks: [], current: 0, total: 0 });

    console.log('[DIAGNOSTIC] Déclenchement scan SSE pour:', selectedAccountId);

    // Utiliser EventSource pour le streaming
    const eventSource = new EventSource(`/api/diagnostic/${selectedAccountId}/scan/stream?token=${token}`);

    eventSource.addEventListener('start', (event) => {
      const data = JSON.parse(event.data);
      console.log('[DIAGNOSTIC] Scan démarré:', data);
    });

    eventSource.addEventListener('progress', (event) => {
      const data = JSON.parse(event.data);
      console.log('[DIAGNOSTIC] Progression:', data);

      if (data.type === 'scan_info') {
        // Info initiale avec le total
        setScanProgress(prev => ({
          ...prev,
          total: data.total,
          allChecks: data.checks
        }));
      } else if (data.type === 'check_start') {
        // Check en cours de traitement
        setScanProgress(prev => ({
          ...prev,
          current: data.current,
          total: data.total,
          currentCheck: data.check
        }));
      } else if (data.type === 'check_complete') {
        // Check terminé
        setScanProgress(prev => ({
          ...prev,
          current: data.current,
          total: data.total,
          currentCheck: null,
          checks: [...(prev?.checks || []), data.check]
        }));
      }
    });

    eventSource.addEventListener('complete', async (event) => {
      const data = JSON.parse(event.data);
      console.log('[DIAGNOSTIC] Scan terminé:', data);
      eventSource.close();

      // D'abord arrêter le mode scan
      setScanning(false);
      setScanProgress(null);

      // Puis recharger les données (avec un petit délai pour s'assurer que les états sont à jour)
      console.log('[DIAGNOSTIC] Rechargement des données pour:', selectedAccountId);
      setTimeout(async () => {
        await fetchLatestScan(selectedAccountId);
        console.log('[DIAGNOSTIC] Données rechargées');
      }, 100);
    });

    eventSource.addEventListener('error', (event) => {
      console.error('[DIAGNOSTIC] Erreur SSE:', event);
      if (event.data) {
        const data = JSON.parse(event.data);
        setError(data.message || 'Erreur lors du scan');
      } else {
        setError('Connexion au serveur perdue');
      }
      eventSource.close();
      setScanning(false);
      setScanProgress(null);
    });

    eventSource.onerror = () => {
      console.error('[DIAGNOSTIC] EventSource error');
      eventSource.close();
      setScanning(false);
      setScanProgress(null);
    };
  };

  // Toggle expand check
  const toggleCheck = (checkId) => {
    setExpandedChecks(prev => ({
      ...prev,
      [checkId]: !prev[checkId]
    }));
  };

  // Toggle collapse family
  const toggleFamily = (family) => {
    setCollapsedFamilies(prev => ({
      ...prev,
      [family]: !prev[family]
    }));
  };

  // Toggle section (skipped/passed) par famille
  const toggleSection = (family, section) => {
    const key = `${family}-${section}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Effet initial
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Réinitialiser les données quand le client change
  useEffect(() => {
    // Réinitialiser tous les états de données quand le client change
    setSelectedAccountId(null);
    setScanData(null);
    setAllScansData({});
    setError(null);
    setExpandedChecks({});
    setCollapsedFamilies({});
  }, [selectedClientId]);

  // Formater l'ID de compte avec tirets (123456789 -> 123-456-789)
  /**
   * Le compte de la liste qui correspond à une valeur d'adresse.
   *
   * L'état porte le `sourceId` tel que la bibliothèque l'écrit, l'adresse sa forme à tirets :
   * comparer les deux sans les normaliser ferait rouvrir le sélecteur à vide au retour.
   * Avant le chargement des comptes, on retient la valeur normalisée — l'effet qui applique
   * `initialAccountId` la rattrapera.
   */
  const compteDepuisLAdresse = (valeur) => {
    if (!valeur) return null;
    const nu = valeur.replace(/-/g, '');
    return accounts.find(c => c.sourceId?.replace(/-/g, '') === nu)?.sourceId || nu;
  };

  const formatAccountIdWithDashes = (id) => {
    if (!id) return null;
    const clean = id.replace(/-/g, '');
    if (clean.length === 10) {
      return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    return id;
  };

  // Notifier App.js du changement de compte pour mettre à jour l'historique dans la sidebar
  // ET mettre à jour l'URL avec l'ID du compte
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('diagnosticAccountChange', {
      detail: { accountId: selectedAccountId }
    }));

    /*
      L'adresse suit le compte — et **entrer dans un compte empile une entrée**.

      Tout passait auparavant par `replaceState` : l'adresse était juste, mais aucune étape ne
      s'inscrivait dans l'historique, et le bouton Précédent sautait par-dessus l'écran de tous
      les comptes pour quitter l'outil. Entrer dans un compte est une navigation : elle s'empile.

      Les deux autres mouvements ne sont pas des navigations et ne s'empilent donc pas :
      — **passer d'un compte à l'autre** par le menu déroulant remplace l'entrée courante, sinon
        parcourir cinq comptes imposerait cinq retours pour revenir à la liste ;
      — **revenir à la liste** dépile, par la même porte que le navigateur (voir les contrôles).

      ⚠️ Le marqueur `vueDiagnostic` est ce qui autorise ce `history.back()`. Sans lui, un lien
      ouvert directement sur un compte — entrée que nous n'avons pas empilée — ferait reculer le
      navigateur hors de l'application.
    */
    const currentParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const clientId = currentParams.get('client');
    // Utiliser le prop isToolsView au lieu de vérifier le hash (qui peut ne pas être encore à jour)
    const hashBase = isToolsView ? '#tools/diagnostic' : '#diagnostic';

    const dansLAdresse = compteDepuisLAdresse(currentParams.get('account'));
    // L'adresse dit déjà ce que l'état dit : c'est elle qui vient de changer, par le bouton
    // Précédent. Il n'y a rien à écrire, et surtout rien à empiler.
    if (dansLAdresse === (selectedAccountId || null)) return;

    const params = new URLSearchParams();
    if (clientId) params.set('client', clientId);
    if (selectedAccountId) params.set('account', formatAccountIdWithDashes(selectedAccountId));
    const newHash = params.toString() ? `${hashBase}?${params.toString()}` : hashBase;
    const etat = { view: isToolsView ? 'tools' : 'diagnostic', clientId, vueDiagnostic: selectedAccountId ? 'compte' : null };

    if (selectedAccountId && !dansLAdresse) {
      window.history.pushState(etat, '', newHash);
    } else if (!selectedAccountId && window.history.state?.vueDiagnostic === 'compte') {
      window.history.back();
    } else if (window.location.hash !== newHash) {
      window.history.replaceState(etat, '', newHash);
    }
  }, [selectedAccountId, isToolsView, accounts]);

  // Appliquer l'initialAccountId quand il change ou quand les comptes sont chargés
  useEffect(() => {
    if (initialAccountId && accounts.length > 0) {
      // Normaliser l'ID (supprimer les tirets) pour la comparaison
      const normalizeId = (id) => id?.replace(/-/g, '') || '';
      const normalizedInitialId = normalizeId(initialAccountId);

      // Trouver le compte correspondant
      const matchingAccount = accounts.find(a => normalizeId(a.sourceId) === normalizedInitialId);

      if (matchingAccount) {
        setSelectedAccountId(matchingAccount.sourceId);
        // Notifier App.js que l'initialAccountId a été utilisé
        if (onInitialAccountUsed) {
          onInitialAccountUsed();
        }
      }
    }
  }, [initialAccountId, accounts, onInitialAccountUsed]);

  // Re-filtrer les comptes quand le client sélectionné change
  // Note: on utilise une ref pour éviter de reset selectedAccountId après l'avoir défini via initialAccountId
  const lastAppliedClientId = React.useRef(selectedClientId);

  useEffect(() => {
    const applyFilter = async () => {
      if (allAccounts.length > 0) {
        setLoadingAccounts(true);
        // Réinitialiser les données de scans pour éviter le flash
        setAllScansData({});

        const filtered = await filterAccountsByClient(allAccounts, selectedClientId);
        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filtered].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        // Réinitialiser la sélection de compte SEULEMENT si le client a changé
        // (pas si c'est juste initialAccountId qui a été consommé)
        const clientChanged = lastAppliedClientId.current !== selectedClientId;
        lastAppliedClientId.current = selectedClientId;

        if (clientChanged && !initialAccountId) {
          setSelectedAccountId(null);
          setScanData(null);
        }

        setLoadingAccounts(false);
      }
    };

    applyFilter();
  }, [selectedClientId, allAccounts, initialAccountId]);

  // Charger les scans quand le compte change ou quand les comptes sont chargés
  useEffect(() => {
    if (selectedAccountId) {
      // Un compte spécifique est sélectionné
      setLoading(true);
      // Si un scan spécifique est sélectionné, charger celui-ci, sinon le dernier
      if (selectedScanId) {
        fetchScanById(selectedScanId);
      } else {
        fetchLatestScan(selectedAccountId);
      }
    } else {
      // "Tous les comptes" est sélectionné - réinitialiser scanData
      setScanData(null);
      if (accounts.length > 0) {
        // Préparer l'état loading AVANT d'appeler fetchAllScans pour éviter le flash
        setLoading(true);
        setAllScansData({});
        fetchAllScans(accounts);
      }
    }
  }, [selectedAccountId, selectedScanId, accounts, fetchLatestScan, fetchScanById, fetchAllScans]);

  // Réinitialiser le scanId sélectionné quand on change de compte
  useEffect(() => {
    if (onScanIdChange) {
      onScanIdChange(null);
    }
  }, [selectedAccountId]);

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Trouver le nom du compte sélectionné
  const selectedAccount = accounts.find(a => a.sourceId === selectedAccountId);

  // Récupérer le nom du client sélectionné
  const selectedClientName = selectedClientId
    ? userClients.find(c => c.id === selectedClientId)?.name || 'Client'
    : null;

  /*
    La sous-vue est locale quand le parent n'en tient pas.

    Cet écran est monté à deux endroits : par `App.js`, qui porte l'état pour pouvoir teinter sa
    barre latérale, et par `ToolsView` — le chemin normal depuis le catalogue — qui ne passe
    **ni `subView` ni `onSubViewChange`**. C'est la seconde raison pour laquelle le choix des
    contrôles était devenu inatteignable : même en reposant le bouton, `onSubViewChange` y était
    `undefined` et le clic ne faisait rien.

    `undefined` et non `null` départage les deux cas : `App.js` passe `null` au repos, ce qui est
    une valeur, et c'est bien son état qu'il faut suivre alors.
  */
  const [sousVueLocale, setSousVueLocale] = useState(null);
  const sousVue = subView !== undefined ? subView : sousVueLocale;
  const changerSousVue = onSubViewChange || setSousVueLocale;

  /*
    Ouvrir et fermer les contrôles en laissant une trace dans l'historique.

    L'écran n'était qu'un changement d'état : l'adresse ne bougeait pas, aucune entrée n'était
    empilée, et le bouton Précédent du navigateur quittait l'outil entier au lieu de revenir au
    diagnostic. Il porte donc `?vue=controles`, empilé par `pushState`.

    ⚠️ **La fermeture passe par `history.back()`, pas par un second `pushState`.** Empiler
    l'adresse précédente laisserait l'écran des contrôles dans l'historique *devant* nous : le
    bouton Précédent l'y rouvrirait, ce qui est exactement l'inverse de ce qu'on corrige. Les
    deux chemins de sortie — le fil d'Ariane et le bouton de l'écran — reviennent ainsi par la
    même porte que le navigateur.

    Le repli direct couvre le cas où l'entrée n'est pas la nôtre : un écran ouvert par un autre
    chemin ne doit pas faire reculer le navigateur dans l'historique de l'utilisateur.
  */
  const adresseDesControles = () => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    params.set('vue', 'controles');
    return `${isToolsView ? '#tools/diagnostic' : '#diagnostic'}?${params.toString()}`;
  };

  const ouvrirControles = () => {
    window.history.pushState({ ...window.history.state, vue: 'controles' }, '', adresseDesControles());
    changerSousVue('checks');
  };

  const fermerControles = () => {
    if (window.history.state?.vue === 'controles') window.history.back();
    else changerSousVue(null);
  };

  /*
    Le bouton Précédent, et le bouton Suivant qui va avec : l'écran suit l'adresse.
  */
  useEffect(() => {
    const auRetour = () => {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      changerSousVue(params.get('vue') === 'controles' ? 'checks' : null);
      // Le compte aussi : sans cela, reculer au-delà des contrôles laissait l'écran afficher un
      // compte que l'adresse ne portait plus.
      setSelectedAccountId(compteDepuisLAdresse(params.get('account')));
    };
    window.addEventListener('popstate', auRetour);
    return () => window.removeEventListener('popstate', auRetour);
    // `accounts` est dans les dépendances : l'écouteur résout le compte de l'adresse dans la
    // liste chargée, et une fermeture figée sur la liste vide rendrait l'identifiant nu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToolsView, accounts]);

  /*
    Le fil d'Ariane de la position courante, remonté au parent.

    En mode Outils, c'est `ToolsView` qui rend le fil, et il s'arrêtait au nom de l'outil : on
    pouvait descendre sur un compte puis sur ses contrôles sans que rien ne le dise, ni ne
    permette de remonter. Cet écran est le seul à savoir où il en est, c'est donc lui qui
    l'annonce.

    Le nom du compte est pris dans la liste quand elle est chargée, et retombe sur son numéro
    sinon — un fil qui attendrait le chargement clignoterait à chaque ouverture.
  */
  const nomDuCompte = selectedAccount?.sourceName || selectedAccountId;

  useEffect(() => {
    if (!onFilChange) return undefined;

    const fil = [];
    if (selectedAccountId) {
      fil.push({
        cle: 'compte',
        libelle: nomDuCompte,
        action: fermerControles,
        titre: 'Revenir au diagnostic de ce compte'
      });
    }
    if (sousVue === 'checks') {
      fil.push({ cle: 'controles', libelle: 'Contrôles' });
    }

    onFilChange(fil);
    // Au démontage, rendre le fil : l'outil suivant ne doit pas hériter de notre position.
    return () => onFilChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, nomDuCompte, sousVue]);

  // Si la sous-vue vaut 'checks', afficher le gestionnaire de checks
  if (sousVue === 'checks') {
    return (
      <div className="diagnostic-view">
        {/* Fil d'Ariane */}
        {!hideBreadcrumb && (
          <div className="app-breadcrumb">
            {selectedClientName && (
              <>
                <span className="breadcrumb-nav-item">
                  <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  {selectedClientName}
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
              </>
            )}
            <span
              className="breadcrumb-nav-item"
              onClick={() => {
                setSelectedAccountId(null);
                // Par la même porte que les autres sorties : l'entrée des contrôles doit être
                // dépilée, pas doublée.
                fermerControles();
              }}
              style={{ cursor: 'pointer' }}
            >
              <Activity size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Diagnostic Google Ads
            </span>
            {selectedAccount && (
              <>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span
                  className="breadcrumb-nav-item"
                  onClick={fermerControles}
                  style={{ cursor: 'pointer' }}
                >
                  {selectedAccount.sourceName || selectedAccount.sourceId}
                </span>
              </>
            )}
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-nav-item active">
              Détail
            </span>
          </div>
        )}

        <DiagnosticChecksManager
          token={token}
          accountId={selectedAccountId}
          onClose={fermerControles}
        />
      </div>
    );
  }

  return (
    <div className="diagnostic-view">
      {/* Fil d'Ariane */}
      {!hideBreadcrumb && (
        <div className="app-breadcrumb">
          {selectedClientName && (
            <>
              <span className="breadcrumb-nav-item">
                <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                {selectedClientName}
              </span>
              <ChevronRight size={14} className="breadcrumb-separator" />
            </>
          )}
          <span
            className={`breadcrumb-nav-item ${!selectedAccountId ? 'active' : ''}`}
            onClick={selectedAccountId ? () => setSelectedAccountId(null) : undefined}
            style={selectedAccountId ? { cursor: 'pointer' } : {}}
          >
            <Activity size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Diagnostic Google Ads
          </span>
          {selectedAccount && (
            <>
              <ChevronRight size={14} className="breadcrumb-separator" />
              <span className="breadcrumb-nav-item active">
                {selectedAccount.sourceName || selectedAccount.sourceId}
              </span>
            </>
          )}
        </div>
      )}

      <div className="diagnostic-content-wrapper">
      <div className="diagnostic-header">
        <div className="diagnostic-header-top">
          <div className="diagnostic-title">
            <Activity size={28} />
            <h1>Diagnostic Google Ads</h1>
          </div>
          {/* Sélecteur de compte */}
          {accounts.length > 0 && (
            <div className="diagnostic-account-selector">
              <select
                value={selectedAccountId || ''}
                onChange={(e) => setSelectedAccountId(e.target.value || null)}
              >
                <option value="">Tous les comptes</option>
                {accounts.map(account => (
                  <option key={account.sourceId} value={account.sourceId}>
                    {account.sourceName || account.sourceId}
                  </option>
                ))}
              </select>

            </div>
          )}
        </div>
        <p className="diagnostic-subtitle">
          Scanner automatique de vos comptes Google Ads
        </p>
      </div>

      {/* Erreur */}
      {error && (
        <div className="diagnostic-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Progression du scan en temps réel */}
      {scanning && scanProgress && (
        <div className="scan-progress-container">
          <div className="scan-progress-header">
            <Loader size={20} className="spin" />
            <span>Scan en cours... {scanProgress.checks?.length || 0}/{scanProgress.total || '?'} checks</span>
          </div>
          <div className="scan-progress-bar">
            <div
              className="scan-progress-fill"
              style={{ width: scanProgress.total ? `${((scanProgress.checks?.length || 0) / scanProgress.total) * 100}%` : '0%' }}
            />
          </div>

          {/* Check en cours */}
          {scanProgress.currentCheck && (
            <div className="scan-current-check">
              <Loader size={14} className="spin" />
              <span>{scanProgress.currentCheck.label}</span>
            </div>
          )}

          <div className="scan-progress-checks">
            {(scanProgress.checks || []).map((check, index) => (
              <div key={index} className={`scan-progress-check status-${check.status.toLowerCase()}`}>
                <span className="check-status-icon">
                  {check.status === 'GREEN' && <CheckCircle size={14} />}
                  {check.status === 'RED' && <AlertCircle size={14} />}
                  {check.status === 'ORANGE' && <AlertTriangle size={14} />}
                  {check.status === 'SKIPPED' && <Clock size={14} />}
                </span>
                <span className="check-label">{check.label}</span>
                <span className="check-summary">{check.summary}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {(loading || loadingAccounts || (accounts.length > 0 && !selectedAccountId && Object.keys(allScansData).length === 0)) && !scanning && (
        <div className="diagnostic-loading">
          <Loader size={32} className="spin" />
          <span>{loadingAccounts ? 'Chargement des comptes...' : 'Chargement du diagnostic...'}</span>
        </div>
      )}

      {/* Pas de comptes */}
      {!loadingAccounts && accounts.length === 0 && (
        /* Le cadre est désormais porté par le composant lui-même, pour que les dix outils
           présentent le même état vide. Le conserver ici l'aurait dessiné deux fois. */
        <AucunCompteGoogleAds token={token} role={user?.role} message="Aucun compte Google Ads" />
      )}

      {/* Vue "Tous les comptes" - Grille de cartes */}
      {!loading && !loadingAccounts && accounts.length > 0 && !selectedAccountId && Object.keys(allScansData).length > 0 && (
        <div className="diagnostic-accounts-grid">
          {accounts.map(account => (
            <AccountCard
              key={account.sourceId}
              accountId={account.sourceId}
              accountName={account.sourceName || account.sourceId}
              scan={allScansData[account.sourceId]?.scan}
              onClick={() => setSelectedAccountId(account.sourceId)}
            />
          ))}
        </div>
      )}

      {/* Pas de scan pour un compte spécifique ou scan sans résultats */}
      {!loading && !loadingAccounts && !scanning && accounts.length > 0 && selectedAccountId && (
        !scanData ||
        !scanData.resultsByFamily ||
        Object.values(scanData.resultsByFamily || {}).every(arr => arr.length === 0)
      ) && (
        <div className="diagnostic-empty">
          <Activity size={48} />
          <h3>Aucun diagnostic disponible</h3>
          <p>Lancez un scan pour analyser ce compte.</p>
          {/* Le même accès avant le premier scan : c'est justement le moment de choisir ce
              qu'on veut contrôler, et sans lui un compte jamais scanné reperdrait l'écran. */}
          <div className="diagnostic-empty-actions">
            <button
              type="button"
              className="diagnostic-controles-btn"
              onClick={ouvrirControles}
              title="Choisir les contrôles à exécuter sur ce compte"
            >
              <SlidersHorizontal size={20} />
              Contrôles
            </button>
          <button
            className="diagnostic-scan-btn"
            onClick={triggerScan}
            disabled={scanning}
          >
            {scanning ? <Loader size={20} className="spin" /> : <RefreshCw size={20} />}
            {scanning ? 'Scan en cours...' : 'Lancer un scan'}
          </button>
          </div>
        </div>
      )}

      {/* Résultats du scan d'un compte spécifique */}
      {!loading && !loadingAccounts && !scanning && selectedAccountId && scanData &&
       scanData.resultsByFamily &&
       !Object.values(scanData.resultsByFamily || {}).every(arr => arr.length === 0) && (
        <div className="diagnostic-content" key={`scan-${scanData.id}-${scanData.scannedAt}`}>
          {/* En-tête avec score */}
          <div className="diagnostic-summary">
            <div className="summary-left">
              <ScoreWidget score={scanData.scoreGlobal} />
            </div>
            <div className="summary-center">
              <div className="summary-account">
                {selectedAccount?.sourceName || selectedAccountId}
              </div>
              <div className="summary-date">
                <Clock size={16} />
                Dernier scan : {formatDate(scanData.scannedAt)}
              </div>
              <div className="summary-counts">
                <span className="count red">
                  <AlertCircle size={16} />
                  {scanData.statusCounts?.RED || 0} critique(s)
                </span>
                <span className="count orange">
                  <AlertTriangle size={16} />
                  {scanData.statusCounts?.ORANGE || 0} avertissement(s)
                </span>
                <span className="count green">
                  <CheckCircle size={16} />
                  {scanData.statusCounts?.GREEN || 0} OK
                </span>
              </div>
            </div>
            <div className="summary-actions">
              {/*
                Le choix des contrôles à exécuter sur ce compte.

                L'écran existe depuis mars (`DiagnosticChecksManager`), et toute sa chaîne était
                restée intacte : les routes `GET/PUT /diagnostic/check-types`, les préférences par
                compte dans `DiagnosticAccountCheckPrefs`, et le filtrage que `DiagnosticService`
                applique avant un scan. Seul le bouton qui l'ouvrait avait disparu — il vivait
                dans une section « Diagnostics » de la barre latérale, retirée le 29 juillet avec
                deux autres sections jugées superflues (6bad358).

                Sa place est à côté du scan : choisir les contrôles et les exécuter sont deux
                temps du même geste.
              */}
              <button
                type="button"
                className="diagnostic-controles-btn"
                onClick={ouvrirControles}
                title="Choisir les contrôles à exécuter sur ce compte"
              >
                <SlidersHorizontal size={20} />
                Contrôles
              </button>
              <button
                className="diagnostic-scan-btn update-btn"
                onClick={triggerScan}
                disabled={scanning}
              >
                {scanning ? <Loader size={20} className="spin" /> : <RefreshCw size={20} />}
                {scanning ? 'Scan en cours...' : 'Mettre à jour'}
              </button>
            </div>
          </div>

          {/* Résultats par famille */}
          <div className="diagnostic-families">
            {['tracking', 'hygiene', 'budget', 'performance'].map(family => {
              const results = scanData.resultsByFamily?.[family] || [];
              const FamilyIcon = FAMILY_ICONS[family];
              const isCollapsed = collapsedFamilies[family];

              // Séparer les checks verts des autres
              const failedChecks = results.filter(r => r.status !== 'GREEN' && r.status !== 'SKIPPED');
              const passedChecks = results.filter(r => r.status === 'GREEN');
              const skippedChecks = results.filter(r => r.status === 'SKIPPED');

              return (
                <div key={family} className="diagnostic-family">
                  <div
                    className="family-header"
                    onClick={() => toggleFamily(family)}
                  >
                    <FamilyIcon size={20} />
                    <h3>{FAMILY_LABELS[family]}</h3>
                    <div className="family-counts">
                      {failedChecks.filter(r => r.status === 'RED').length > 0 && (
                        <span className="family-count red">
                          {failedChecks.filter(r => r.status === 'RED').length}
                        </span>
                      )}
                      {failedChecks.filter(r => r.status === 'ORANGE').length > 0 && (
                        <span className="family-count orange">
                          {failedChecks.filter(r => r.status === 'ORANGE').length}
                        </span>
                      )}
                    </div>
                    {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                  </div>

                  {!isCollapsed && (
                    <div className="family-content">
                      {/* Checks échoués */}
                      {failedChecks.map(result => (
                        <DiagnosticCheckRow
                          key={result.id}
                          result={result}
                          isExpanded={expandedChecks[result.id]}
                          onToggle={() => toggleCheck(result.id)}
                          onCorriger={(r, action) => setCorrection({ result: r, action })}
                        />
                      ))}

                      {/* Checks skipped */}
                      {skippedChecks.length > 0 && (
                        <div className="checks-skipped">
                          <div
                            className="skipped-header"
                            onClick={() => toggleSection(family, 'skipped')}
                            style={{ cursor: 'pointer' }}
                          >
                            <Clock size={16} />
                            {skippedChecks.length} check(s) non exécuté(s)
                            {expandedSections[`${family}-skipped`] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                          {expandedSections[`${family}-skipped`] && (
                            <div className="section-checks-list">
                              {skippedChecks.map(result => (
                                <div key={result.id} className="section-check-item skipped">
                                  <span className="section-check-label">{result.label || result.checkKey || 'Check inconnu'}</span>
                                  <span className="section-check-reason">{result.detail?.summary || 'Données insuffisantes'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Checks passés */}
                      {passedChecks.length > 0 && (
                        <div className="checks-passed">
                          <div
                            className="passed-header"
                            onClick={() => toggleSection(family, 'passed')}
                            style={{ cursor: 'pointer' }}
                          >
                            <CheckCircle size={16} color="#22c55e" />
                            {passedChecks.length} check(s) OK
                            {expandedSections[`${family}-passed`] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                          {expandedSections[`${family}-passed`] && (
                            <div className="section-checks-list">
                              {passedChecks.map(result => (
                                <div key={result.id} className="section-check-item passed">
                                  <span className="section-check-label">{result.label || result.checkKey || 'Check inconnu'}</span>
                                  <span className="section-check-reason">{result.detail?.summary || 'OK'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {results.length === 0 && (
                        <div className="family-empty">
                          Aucun check dans cette catégorie
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>

      {/*
        La **même** modale que Radar : le geste doit être identique d'un écran à l'autre, et
        l'aperçu de sûreté ne doit pas exister en deux versions.

        Le résultat d'un contrôle n'a pas la forme d'une ligne de feed — il ne porte pas son
        compte, et son détail est imbriqué —, d'où cette traduction. La faire ici plutôt que de
        rendre la modale tolérante aux deux formes : un composant qui accepte deux formes finit
        par n'en gérer correctement aucune.
      */}
      {correction && (
        <ModaleCorrection
          reco={{
            id: correction.result.id,
            tool: 'GOOGLE_ADS',
            accountId: selectedAccountId,
            checkKey: correction.result.checkKey,
            label: correction.result.label,
            status: correction.result.status,
            compte: accounts.find(c => c.accountId === selectedAccountId || c.sourceId === selectedAccountId) || null,
            affectedItems: correction.result.detail?.affectedItems || [],
            affectedCount: correction.result.detail?.affectedCount ?? (correction.result.detail?.affectedItems || []).length,
            affectedComplet: correction.result.detail?.affectedComplet ?? true
          }}
          action={correction.action}
          token={token}
          origine="DIAGNOSTIC"
          onFermer={() => setCorrection(null)}
          onCorrige={() => {
            // Le contrôle a été rejoué côté serveur : on recharge le scan pour afficher le
            // constat à jour, au lieu de retirer la ligne — ici, contrairement à Radar, la
            // ligne doit rester et passer au vert.
            setCorrection(null);
            if (selectedAccountId) fetchLatestScan(selectedAccountId);
          }}
        />
      )}
    </div>
  );
};

export default DiagnosticView;
